/** Central domain enums. Stored as stable string codes (not display labels). */

export const INDUSTRIES = [
  'hvac', 'plumbing', 'electrical', 'roofing', 'general_contracting', 'landscaping',
  'pest_control', 'garage_door', 'painting', 'concrete', 'flooring', 'cleaning',
  'handyman', 'restoration', 'appliance_repair', 'pool_spa', 'locksmith', 'solar',
  'fire_security', 'other',
] as const;
export type Industry = (typeof INDUSTRIES)[number];

export const USER_ROLES = [
  'owner', 'admin', 'manager', 'dispatcher', 'office', 'sales', 'technician', 'read_only',
] as const;
export type UserRole = (typeof USER_ROLES)[number];

export const USER_STATUS = ['active', 'invited', 'deactivated'] as const;
export type UserStatus = (typeof USER_STATUS)[number];

export const CUSTOMER_TYPE = ['residential', 'commercial'] as const;
export type CustomerType = (typeof CUSTOMER_TYPE)[number];

export const LEAD_SOURCES = [
  'website', 'phone', 'google', 'facebook', 'referral', 'repeat_customer', 'partner', 'manual', 'other',
] as const;
export type LeadSource = (typeof LEAD_SOURCES)[number];

export const LEAD_STAGES = ['new', 'contacted', 'qualified', 'quoted', 'won', 'lost'] as const;
export type LeadStage = (typeof LEAD_STAGES)[number];

export const ESTIMATE_STATUS = [
  'draft', 'sent', 'viewed', 'approved', 'declined', 'converted', 'expired',
] as const;
export type EstimateStatus = (typeof ESTIMATE_STATUS)[number];

export const JOB_STATUS = [
  'new', 'needs_scheduling', 'scheduled', 'dispatched', 'en_route', 'arrived',
  'in_progress', 'paused', 'completed', 'canceled',
] as const;
export type JobStatus = (typeof JOB_STATUS)[number];

export const JOB_PRIORITY = ['low', 'normal', 'high', 'emergency'] as const;
export type JobPriority = (typeof JOB_PRIORITY)[number];

export const APPOINTMENT_STATUS = ['scheduled', 'completed', 'canceled', 'no_show'] as const;
export type AppointmentStatus = (typeof APPOINTMENT_STATUS)[number];

export const INVOICE_STATUS = [
  'draft', 'sent', 'viewed', 'partially_paid', 'paid', 'past_due', 'void',
] as const;
export type InvoiceStatus = (typeof INVOICE_STATUS)[number];

export const PAYMENT_STATUS = ['pending', 'succeeded', 'failed', 'refunded', 'partially_refunded'] as const;
export type PaymentStatus = (typeof PAYMENT_STATUS)[number];

export const PAYMENT_METHOD = ['card', 'ach', 'cash', 'check', 'wallet', 'other'] as const;
export type PaymentMethod = (typeof PAYMENT_METHOD)[number];

export const PRICEBOOK_KIND = ['service', 'material', 'equipment', 'labor'] as const;
export type PricebookKind = (typeof PRICEBOOK_KIND)[number];

export const LINE_ITEM_KIND = ['service', 'material', 'labor', 'discount', 'fee', 'custom'] as const;
export type LineItemKind = (typeof LINE_ITEM_KIND)[number];

export const MESSAGE_CHANNEL = ['email', 'sms', 'mms', 'call', 'note', 'system'] as const;
export type MessageChannel = (typeof MESSAGE_CHANNEL)[number];

export const MESSAGE_DIRECTION = ['inbound', 'outbound', 'internal'] as const;
export type MessageDirection = (typeof MESSAGE_DIRECTION)[number];

export const MESSAGE_DELIVERY = ['queued', 'sent', 'delivered', 'failed', 'bounced', 'read'] as const;
export type MessageDelivery = (typeof MESSAGE_DELIVERY)[number];

export const AUTOMATION_TRIGGERS = [
  'lead_created', 'estimate_sent', 'estimate_viewed', 'estimate_approved', 'estimate_declined',
  'job_scheduled', 'job_completed', 'invoice_created', 'invoice_overdue', 'payment_received',
  'customer_inactive',
] as const;
export type AutomationTrigger = (typeof AUTOMATION_TRIGGERS)[number];

export const AUTOMATION_ACTIONS = [
  'send_email', 'send_sms', 'create_task', 'change_status', 'assign_employee',
  'create_follow_up', 'request_review',
] as const;
export type AutomationAction = (typeof AUTOMATION_ACTIONS)[number];

export const OPPORTUNITY_STAGES = ['open', 'quoting', 'negotiating', 'won', 'lost'] as const;
export type OpportunityStage = (typeof OPPORTUNITY_STAGES)[number];

/** Entities that support tenant-defined custom fields. */
export const CUSTOM_FIELD_ENTITIES = ['customer', 'property', 'lead', 'job', 'equipment'] as const;
export type CustomFieldEntity = (typeof CUSTOM_FIELD_ENTITIES)[number];

export const CUSTOM_FIELD_TYPES = ['text', 'number', 'date', 'select', 'multiselect', 'checkbox'] as const;
export type CustomFieldType = (typeof CUSTOM_FIELD_TYPES)[number];

/** Field types available in tenant-configurable forms/checklists. */
export const FORM_FIELD_TYPES = ['text', 'number', 'date', 'select', 'checkbox', 'photo', 'signature', 'passfail'] as const;
export type FormFieldType = (typeof FORM_FIELD_TYPES)[number];

export const EXPENSE_CATEGORIES = ['materials', 'labor', 'permit', 'subcontractor', 'equipment_rental', 'fuel', 'other'] as const;
export type ExpenseCategory = (typeof EXPENSE_CATEGORIES)[number];

export const BILLING_INTERVAL = ['weekly', 'monthly', 'quarterly', 'semiannual', 'annual'] as const;
export type BillingInterval = (typeof BILLING_INTERVAL)[number];

export const SUBSCRIPTION_STATUS = [
  'trialing', 'active', 'past_due', 'canceled', 'incomplete',
] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUS)[number];

export const PLAN_TIERS = ['solo', 'team', 'growth'] as const;
export type PlanTier = (typeof PLAN_TIERS)[number];

/** Allowed status transitions for a job (server-enforced). */
export const JOB_TRANSITIONS: Record<JobStatus, readonly JobStatus[]> = {
  new: ['needs_scheduling', 'scheduled', 'canceled'],
  needs_scheduling: ['scheduled', 'canceled'],
  scheduled: ['dispatched', 'needs_scheduling', 'canceled'],
  dispatched: ['en_route', 'scheduled', 'canceled'],
  en_route: ['arrived', 'dispatched', 'canceled'],
  arrived: ['in_progress', 'canceled'],
  in_progress: ['paused', 'completed', 'canceled'],
  paused: ['in_progress', 'completed', 'canceled'],
  completed: [],
  canceled: [],
};

export function canTransitionJob(from: JobStatus, to: JobStatus): boolean {
  return JOB_TRANSITIONS[from].includes(to);
}
